import React, { useState } from "react";
import { Card, Button, message, Modal } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { SwitchButton } from "@/components/UI/Buttons";
import AddPaymentModal from "@/components/UI/Modals/AddPaymentModal";
import EditPaymentModal from "@/components/UI/Modals/EditPaymentModal";
import PaymentMethodMenu from "@/components/UI/Menus/PaymentMethodMenu";
import {
  useGetPaymentOptionsQuery,
  useAddPaymentOptionMutation,
  useUpdatePaymentOptionMutation,
  useTogglePaymentOptionStatusMutation,
  useDeletePaymentOptionMutation,
} from "@/services/api/payment/paymentApi";

const PaymentSetup = () => {
  // State for modals
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  // API hooks
  const {
    data: paymentOptions = [],
    isLoading,
    error,
    refetch,
  } = useGetPaymentOptionsQuery();
  const [addPaymentOption, { isLoading: isAdding }] =
    useAddPaymentOptionMutation();
  const [updatePaymentOption, { isLoading: isUpdating }] =
    useUpdatePaymentOptionMutation();
  const [togglePaymentStatus, { isLoading: isToggling }] =
    useTogglePaymentOptionStatusMutation();
  const [deletePaymentOption, { isLoading: isDeleting }] =
    useDeletePaymentOptionMutation();

  // Handle adding new payment method
  const handleAddPayment = async (formData) => {
    try {
      await addPaymentOption(formData).unwrap();
      message.success("Payment method added successfully!");
      refetch();
    } catch (error) {
      message.error(error?.data?.message || "Failed to add payment method");
      throw error;
    }
  };

  // Handle updating payment method
  const handleUpdatePayment = async (id, formData) => {
    try {
      await updatePaymentOption({ id, requestData: formData }).unwrap();
      message.success("Payment method updated successfully!");
      refetch();
    } catch (error) {
      message.error(error?.data?.message || "Failed to update payment method");
      throw error;
    }
  };

  // Handle toggling payment status with optimistic updates
  const handleToggleStatus = async (paymentMethod) => {
    const newStatus = paymentMethod.status === 1 ? 0 : 1;

    try {
      // Optimistic update
      const optimisticData = paymentOptions.map((payment) =>
        payment.id === paymentMethod.id
          ? { ...payment, status: newStatus }
          : payment
      );

      // Update cache optimistically
      // Note: This would require RTK Query cache manipulation in a real app

      await togglePaymentStatus({
        id: paymentMethod.id,
        requestData: { status: newStatus },
      }).unwrap();

      message.success(
        newStatus === 1
          ? "Payment method activated successfully!"
          : "Payment method deactivated successfully!"
      );
      refetch();
    } catch (error) {
      message.error(error?.data?.message || "Failed to update payment status");
      // Revert optimistic update on error
      refetch();
    }
  };

  // Handle edit payment method
  const handleEditPayment = (paymentMethod) => {
    setSelectedPayment(paymentMethod);
    setIsEditModalOpen(true);
  };

  // Handle delete payment method
  const handleDeletePayment = (paymentMethod) => {
    Modal.confirm({
      title: "Delete Payment Method",
      content: `Are you sure you want to delete "${paymentMethod.name}"? This action cannot be undone.`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deletePaymentOption(paymentMethod.id).unwrap();
          message.success("Payment method deleted successfully!");
          refetch();
        } catch (error) {
          message.error(
            error?.data?.message || "Failed to delete payment method"
          );
        }
      },
    });
  };

  // Render payment method icon
  // Update the renderPaymentIcon function
  const renderPaymentIcon = (paymentMethod) => {
    if (paymentMethod.url) {
      return (
        <div className="w-8 h-8 flex items-center justify-center">
          <img
            src={paymentMethod.url}
            alt={paymentMethod.name}
            className="w-full h-full object-cover rounded-lg"
          />
        </div>
      );
    }

    // Default icon based on payment method name
    const name = paymentMethod.name.toLowerCase();
    if (name.includes("paystack")) {
      return (
        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
          <div className="w-4 h-4 bg-blue-600 rounded-sm flex flex-col justify-center items-center">
            <div className="w-2 h-0.5 bg-white rounded-sm mb-0.5"></div>
            <div className="w-3 h-0.5 bg-white rounded-sm mb-0.5"></div>
            <div className="w-2.5 h-0.5 bg-white rounded-sm"></div>
          </div>
        </div>
      );
    } else if (name.includes("opay")) {
      return (
        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
          <div className="w-4 h-4 bg-green-600 rounded-full flex items-center justify-center">
            <div className="w-2 h-2 bg-white rounded-full"></div>
          </div>
        </div>
      );
    } else if (name.includes("flutterwave")) {
      return (
        <div className="w-8 h-8 bg-gradient-to-br from-orange-400 via-blue-500 to-green-400 rounded-lg flex items-center justify-center flex-shrink-0">
          <div className="w-4 h-4 bg-white rounded-sm transform rotate-45"></div>
        </div>
      );
    } else {
      // Generic icon for other payment methods
      return (
        <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
          <div className="w-4 h-4 bg-gray-600 rounded-sm flex items-center justify-center">
            <span className="text-white text-xs font-bold">
              {paymentMethod.name.charAt(0).toUpperCase()}
            </span>
          </div>
        </div>
      );
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
        {/* Left Section - Empty for layout balance */}
        {/* Header Section */}
        <div className="mb-4">
          <h2 className="text-xl font-bold text-gray-800 mb-2">
            Payment Methods
          </h2>
          <p className="text-gray-600 text-sm mb-4">
            Configure how customers can pay for their orders
          </p>

          {/* Add Payment Method Button - Only show when there are existing payment methods */}
          {paymentOptions.length > 0 && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsAddModalOpen(true)}
              className="bg-orange-500 hover:bg-orange-600 border-orange-500 hover:border-orange-600 rounded-lg px-6 flex items-center justify-center"
              size="large"
            >
              Add New
            </Button>
          )}
        </div>

        {/* Right Section - Payment Method Cards */}
        <div className="space-y-4">
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto"></div>
              <p className="text-gray-600 mt-2">Loading payment methods...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600 mb-4">Failed to load payment methods</p>
              <div className="flex items-center justify-center gap-3">
                <Button onClick={refetch}>Try Again</Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setIsAddModalOpen(true)}
                  className="bg-orange-500 hover:bg-orange-600 border-orange-500 hover:border-orange-600"
                >
                  Add Payment Method
                </Button>
              </div>
            </div>
          ) : paymentOptions.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <PlusOutlined className="text-2xl text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                No Payment Methods
              </h3>
              <p className="text-gray-600 mb-4">
                Get started by adding your first payment method
              </p>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setIsAddModalOpen(true)}
                className="bg-orange-500 hover:bg-orange-600 border-orange-500 hover:border-orange-600"
              >
                Add Payment Method
              </Button>
            </div>
          ) : (
            paymentOptions.map((paymentMethod) => (
              <Card
                key={paymentMethod.id}
                className={`border rounded-lg shadow-sm hover:shadow-md transition-all duration-200 ${paymentMethod.status === 1
                  ? "border-green-200 bg-green-50"
                  : "border-gray-200 bg-white"
                  }`}
                styles={{ body: { padding: "20px" } }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 flex-1">
                    {renderPaymentIcon(paymentMethod)}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-base font-semibold text-gray-800 m-0 p-0">
                          {paymentMethod.name}
                        </h3>
                        <span
                          className={`px-2 py-1 text-[8px] rounded-full ${paymentMethod.status === 1
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-600"
                            }`}
                        >
                          {paymentMethod.status === 1 ? "Active" : "Inactive"}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 m-0 p-0">
                        {paymentMethod.description}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                    <div className="flex items-center">
                      <SwitchButton
                        checked={paymentMethod.status === 1}
                        onChange={() => handleToggleStatus(paymentMethod)}
                        disabled={isToggling}
                      />
                    </div>
                    <div className="flex items-center">
                      <PaymentMethodMenu
                        paymentMethod={paymentMethod}
                        onEdit={handleEditPayment}
                        onDelete={handleDeletePayment}
                        onToggleStatus={handleToggleStatus}
                        isUpdating={isToggling}
                        isDeleting={isDeleting}
                      />
                    </div>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Modals */}
      <AddPaymentModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleAddPayment}
        loading={isAdding}
      />

      <EditPaymentModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedPayment(null);
        }}
        onSubmit={handleUpdatePayment}
        loading={isUpdating}
        paymentData={selectedPayment}
      />
    </div>
  );
};

export default PaymentSetup;
